// Relatórios de atendimentos (/reports) no padrão Aeros (mockup "Tela Aeros
// Dark - Relatórios"): header 60px com ações, card de filtros (presets de
// período, pills, busca, status), 5 KPIs (GET /ticketreport/summary), tabela
// em CSS grid e paginação. Dados: GET /ticketreport/reports (mesmos params de
// sempre + walletId). Regras puras em ./reportRules.js.
import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import EventIcon from "@material-ui/icons/Event";
import SearchIcon from "@material-ui/icons/Search";
import BookmarkBorderIcon from "@material-ui/icons/BookmarkBorder";
import PictureAsPdfOutlinedIcon from "@material-ui/icons/PictureAsPdfOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import HistoryIcon from "@material-ui/icons/History";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ConfirmationNumberOutlinedIcon from "@material-ui/icons/ConfirmationNumberOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import TimerIcon from "@material-ui/icons/Timer";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import SentimentSatisfiedIcon from "@material-ui/icons/SentimentSatisfied";
import SentimentDissatisfiedIcon from "@material-ui/icons/SentimentDissatisfied";
import SentimentSatisfiedAltIcon from "@material-ui/icons/SentimentSatisfiedAlt";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import html2pdf from "html2pdf.js";

import MainContainer from "../../components/MainContainer";
import ReportsNav from "../../components/ReportsNav";
import useAerosSettingsStyles from "../../components/Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { initialsOf, avatarColorFor } from "../Contacts/contactRules";
import TicketReportModal from "./TicketReportModal";
import ExportReportModal from "./ExportReportModal";
import SaveFilterModal from "./SaveFilterModal";
import exportXlsx from "./exportXlsx";
import { i18n } from "../../translate/i18n";
import {
  PRESETS, presetRange, detectPreset, DEFAULT_FILTERS, hasActiveFilters, statusMeta, STATUS_PILLS,
  formatDuration, formatMinutes, npsMeta, saleCell, shortDate, updatedAgo, periodLabel, pageRange, delta,
} from "./reportRules";

const GRID = "64px 1.8fr 1fr 1.2fr 1.1fr 1fr 1.3fr 1fr 0.8fr 0.9fr 84px";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  const line = isLight ? theme.palette.divider : "rgba(255,255,255,0.06)";
  const purple = stateText(theme, "#C08BFF", "purple");
  const tone = (hex, key) => ({ color: stateText(theme, hex, key), backgroundColor: alpha(hex, 0.1) });
  return {
    pageHeader: { minHeight: 60, flexShrink: 0, display: "flex", alignItems: "center", gap: 12, padding: "0 12px", borderBottom: `1px solid ${line}` },
    pageTitle: { fontFamily: AEROS.fontDisplay, fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em", color: theme.palette.text.primary, lineHeight: 1.3, "& span": { color: theme.palette.text.secondary, fontWeight: 500 } },
    pageSubtitle: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.4 },
    actions: { marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" },
    pageBody: { flex: 1, minHeight: 0, padding: "16px 12px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14, "& > *": { flexShrink: 0 }, ...theme.scrollbarStyles },
    card: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${line}`, borderRadius: 14, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 },
    row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
    segmented: { display: "flex", border: `1px solid ${border}`, borderRadius: 10, overflow: "hidden", fontSize: 12, fontWeight: 600, flex: "none" },
    seg: { padding: "8px 12px", color: theme.palette.text.secondary, background: "none", border: "none", cursor: "pointer", font: "inherit" },
    segOn: { backgroundColor: alpha("#A044FF", 0.2), color: purple },
    pill: { display: "flex", alignItems: "center", gap: 6, backgroundColor: theme.palette.background.paper, border: `1px solid ${border}`, borderRadius: 10, padding: "8px 12px", fontSize: 12.5, color: theme.palette.text.secondary, whiteSpace: "nowrap" },
    pillOn: { borderColor: alpha(AEROS.cyan, 0.35) },
    pillIcon: { fontSize: 16, color: theme.palette.text.secondary },
    pillSelect: { fontSize: 12.5, color: theme.palette.text.primary, fontWeight: 600, "& .MuiNativeSelect-select": { padding: 0, paddingRight: 20, background: "transparent", "&:focus": { background: "transparent" } }, "& .MuiNativeSelect-icon": { fontSize: 18, color: theme.palette.aeros.textTertiary, top: "calc(50% - 9px)" } },
    dateField: { background: "transparent", border: "none", outline: "none", padding: 0, font: "inherit", fontSize: 12.5, color: theme.palette.text.primary, colorScheme: theme.palette.type, fontVariantNumeric: "tabular-nums" },
    divider: { width: 1, height: 24, backgroundColor: isLight ? theme.palette.divider : "rgba(255,255,255,0.08)" },
    toggleRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.palette.text.secondary, whiteSpace: "nowrap" },
    clear: { marginLeft: "auto", fontSize: 12, fontWeight: 600, color: stateText(theme, AEROS.cyan, "info"), background: "none", border: "none", cursor: "pointer", font: "inherit", whiteSpace: "nowrap" },
    search: { flex: 1, display: "flex", gap: 8, alignItems: "center", backgroundColor: theme.palette.background.paper, border: `1px solid ${border}`, borderRadius: 10, padding: "8px 12px", fontSize: 13, color: theme.palette.text.secondary, minWidth: 220, "& input": { flex: 1, background: "none", border: "none", outline: "none", font: "inherit", color: theme.palette.text.primary, "&::placeholder": { color: theme.palette.aeros.textTertiary } }, "& .MuiSvgIcon-root": { fontSize: 17 } },
    statusPills: { display: "flex", gap: 6 },
    sPill: { borderRadius: 99, padding: "5px 12px", fontSize: 11.5, fontWeight: 600, color: theme.palette.text.secondary, border: `1px solid ${border}`, background: "none", cursor: "pointer", font: "inherit", display: "flex", gap: 5, alignItems: "center" },
    sPillOn: { color: purple, backgroundColor: alpha("#A044FF", 0.15), borderColor: "transparent" },
    kpis: { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 14, [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr 1fr" } },
    kpi: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${line}`, borderRadius: 14, padding: "16px 18px" },
    kpiHead: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.palette.text.secondary, fontWeight: 500, "& .MuiSvgIcon-root": { fontSize: 17 } },
    kpiValue: { fontSize: 24, fontWeight: 700, letterSpacing: "-0.03em", marginTop: 8, fontVariantNumeric: "tabular-nums", color: theme.palette.text.primary },
    kpiDelta: { fontSize: 11.5, fontWeight: 600, marginTop: 3, color: theme.palette.text.secondary, minHeight: 15 },
    tHead: { display: "grid", gridTemplateColumns: GRID, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary, borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`, backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.02)", "& > div": { padding: "12px 8px" }, "& > div:first-child": { paddingLeft: 20 }, "& > div:last-child": { paddingRight: 20, textAlign: "right" } },
    tRow: { display: "grid", gridTemplateColumns: GRID, alignItems: "center", borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`, fontSize: 13, fontVariantNumeric: "tabular-nums", color: theme.palette.text.primary, "&:hover": { backgroundColor: alpha("#A044FF", 0.04) }, "& > div": { padding: "10px 8px", minWidth: 0 }, "& > div:first-child": { paddingLeft: 20 }, "& > div:last-child": { paddingRight: 20 } },
    tableWrap: { overflowX: "auto", "& > div": { minWidth: 1100 } },
    id: { color: theme.palette.text.secondary, fontWeight: 600 },
    contact: { display: "flex", alignItems: "center", gap: 9 },
    av: { width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#07080D", flex: "none" },
    avSm: { width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 800, color: "#07080D", flex: "none" },
    ell: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    name: { fontWeight: 600 },
    sub: { fontSize: 10.5, color: theme.palette.aeros.textTertiary },
    chip: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, borderRadius: 5, padding: "2px 8px", maxWidth: "100%" },
    secondary: { fontSize: 12.5, color: theme.palette.text.secondary, display: "flex", alignItems: "center", gap: 7 },
    status: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, borderRadius: 99, padding: "3px 10px", whiteSpace: "nowrap", "& i": { width: 6, height: 6, borderRadius: "50%", background: "currentColor" } },
    info: tone(AEROS.cyan, "info"), success: tone(AEROS.success, "success"), warning: tone(AEROS.warning, "warning"), error: tone(AEROS.error, "error"), purple: tone("#C08BFF", "purple"),
    muted: { color: theme.palette.text.secondary, backgroundColor: isLight ? theme.palette.action.hover : "rgba(255,255,255,0.06)" },
    dates: { fontSize: 11.5, color: theme.palette.text.secondary, lineHeight: 1.4, "& span": { color: stateText(theme, AEROS.cyan, "info") } },
    nps: { display: "flex", alignItems: "center", gap: 4, fontSize: 12.5, fontWeight: 700, "& .MuiSvgIcon-root": { fontSize: 14 } },
    dash: { fontSize: 11.5, color: theme.palette.aeros.textTertiary },
    sold: { fontSize: 12, fontWeight: 700, color: stateText(theme, AEROS.success, "success"), whiteSpace: "nowrap" },
    notSold: { fontSize: 11, color: stateText(theme, AEROS.error, "error"), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    rowActions: { display: "flex", justifyContent: "flex-end", gap: 6 },
    actBtn: { width: 30, height: 30, borderRadius: 8, border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.08)"}`, color: theme.palette.text.secondary, padding: 0, "& .MuiSvgIcon-root": { fontSize: 16 }, "&:hover": { color: AEROS.cyan, borderColor: alpha(AEROS.cyan, 0.4) } },
    foot: { display: "flex", alignItems: "center", padding: "12px 20px", fontSize: 12, color: theme.palette.text.secondary, gap: 14 },
    pageSize: { backgroundColor: theme.palette.background.paper, border: `1px solid ${border}`, borderRadius: 8, padding: "2px 10px", fontSize: 11.5, display: "flex", alignItems: "center", gap: 4 },
    pages: { marginLeft: "auto", display: "flex", gap: 4 },
    pg: { width: 28, height: 28, borderRadius: 8, border: `1px solid ${border}`, display: "flex", alignItems: "center", justifyContent: "center", background: "none", cursor: "pointer", font: "inherit", fontSize: 12, color: theme.palette.text.primary, "&:disabled": { opacity: 0.4, cursor: "default" } },
    pgOn: { backgroundColor: alpha("#A044FF", 0.2), color: purple, fontWeight: 700, borderColor: "transparent" },
    dots: { fontSize: 12, color: theme.palette.aeros.textTertiary, padding: "0 4px", display: "flex", alignItems: "center" },
    empty: { padding: "40px 20px", textAlign: "center", fontSize: 13, color: theme.palette.text.secondary },
    savedItem: { fontSize: 13, display: "flex", alignItems: "center", gap: 8, minWidth: 220, "& .MuiSvgIcon-root": { fontSize: 16, marginLeft: "auto", color: theme.palette.aeros.textTertiary } },
  };
});

const KPI_ICONS = { total: ConfirmationNumberOutlinedIcon, resolution: CheckCircleOutlineIcon, wait: HourglassEmptyIcon, duration: TimerIcon, nps: StarBorderIcon };

const t = (k, o) => i18n.t(`aeros.reports.${k}`, o);

const paramsOf = (f) => ({
  searchParam: f.searchParam || undefined,
  whatsappId: JSON.stringify(f.whatsappIds),
  users: JSON.stringify(f.userIds),
  queueIds: JSON.stringify(f.queueIds),
  status: JSON.stringify(f.status),
  walletId: f.walletId || undefined,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  onlyRated: f.onlyRated ? "true" : "false",
});

const Reports = () => {
  const classes = useStyles();
  const aeros = useAerosSettingsStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [filters, setFilters] = useState(() => DEFAULT_FILTERS());
  const [applied, setApplied] = useState(() => DEFAULT_FILTERS());
  const [lists, setLists] = useState({ queues: [], users: [], whatsapps: [] });
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [summary, setSummary] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [tick, setTick] = useState(0);
  const [ticketOpen, setTicketOpen] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState([]);
  const [savedAnchor, setSavedAnchor] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/queue"), api.get("/users/list"), api.get("/whatsapp")])
      .then(([q, u, w]) => setLists({ queues: q.data || [], users: Array.isArray(u.data) ? u.data : u.data?.users || [], whatsapps: w.data || [] }))
      .catch(toastError);
    api.get("/report-filters").then(({ data }) => setSaved(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 60000); return () => clearInterval(t); }, []);

  const load = useCallback(async (f, p, size) => {
    setLoading(true);
    try {
      const [{ data }, sum] = await Promise.all([
        api.get("/ticketreport/reports", { params: { ...paramsOf(f), page: p, pageSize: size } }),
        api.get("/ticketreport/summary", { params: paramsOf(f) }).then((r) => r.data).catch(() => null),
      ]);
      setTickets(data.tickets || []);
      setTotal(Number(data.totalTickets?.total || 0));
      setSummary(sum);
      setApplied(f);
      setPage(p);
      setUpdatedAt(new Date());
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filters, 1, pageSize); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (f = filters, p = 1, size = pageSize) => { setFilters(f); load(f, p, size); };
  const set = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const preset = detectPreset(filters.dateFrom, filters.dateTo);
  const toggleIn = (key, id) => set({ [key]: filters[key].includes(id) ? filters[key].filter((x) => x !== id) : [...filters[key], id] });
  const single = (key, val) => set({ [key]: val ? [val] : [] });

  const kpis = useMemo(() => {
    const s = summary || {};
    const prev = s.previous || {};
    const pct = (a, b) => (b ? Math.round((a / b) * 100) : null);
    return [
      { key: "total", label: t("kpis.total"), value: s.total ?? total, d: delta(s.total, prev.total), accent: AEROS.cyan },
      { key: "resolution", label: t("kpis.resolution"), value: pct(s.closed, s.total) != null ? `${pct(s.closed, s.total)}%` : "—", d: delta(pct(s.closed, s.total), pct(prev.closed, prev.total), { unit: " p.p.", suffix: "" }), accent: AEROS.success },
      { key: "wait", label: t("kpis.wait"), value: formatMinutes(s.avgWaitMinutes), d: delta(s.avgWaitMinutes, prev.avgWaitMinutes, { unit: " min", lowerIsBetter: true, suffix: "" }), accent: AEROS.warning },
      { key: "duration", label: t("kpis.duration"), value: formatMinutes(s.avgDurationMinutes), d: delta(s.avgDurationMinutes, prev.avgDurationMinutes, { unit: " min", lowerIsBetter: true, suffix: "" }), accent: "#C08BFF" },
      { key: "nps", label: t("kpis.nps"), value: s.avgNps != null ? Number(s.avgNps).toLocaleString(undefined, { maximumFractionDigits: 1 }) : "—", d: s.rated != null ? { text: t("kpis.rated", { rated: s.rated, pct: pct(s.rated, s.total) ?? 0 }), tone: "muted" } : null, accent: AEROS.warning },
    ];
  }, [summary, total]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = summary ? { total: summary.total, open: summary.open, pending: summary.pending, closed: summary.closed } : {};

  const filterChips = () => {
    const c = [{ label: `${t("filters.period")}: ${preset ? PRESETS.find((p) => p.key === preset).label : periodLabel(filters.dateFrom, filters.dateTo)}` }];
    const nameOf = (list, id) => list.find((x) => Number(x.id) === Number(id))?.name;
    filters.queueIds.forEach((id) => c.push({ label: `${t("filters.queue")}: ${nameOf(lists.queues, id) || id}`, tone: "info" }));
    filters.userIds.forEach((id) => c.push({ label: `${t("filters.user")}: ${nameOf(lists.users, id) || id}` }));
    filters.whatsappIds.forEach((id) => c.push({ label: `${t("filters.connection")}: ${nameOf(lists.whatsapps, id) || id}` }));
    filters.status.forEach((s) => c.push({ label: `${t("filters.status")}: ${STATUS_PILLS.find((p) => p.key === s)?.label || s}`, tone: "success" }));
    if (filters.walletId) c.push({ label: `${t("filters.wallet")}: ${nameOf(lists.users, filters.walletId) || filters.walletId}` });
    if (filters.onlyRated) c.push({ label: t("filters.onlyRated") });
    if (filters.searchParam) c.push({ label: `${t("filters.search")}: ${filters.searchParam}` });
    return c;
  };

  const fetchAll = async (f) => {
    const { data } = await api.get("/ticketreport/reports", { params: { ...paramsOf(f), page: 1, pageSize: 9999999 } });
    return data.tickets || [];
  };

  const handleExport = async ({ format, columns, transcript }) => {
    setExporting(true);
    try {
      if (format === "pdf") {
        const el = document.getElementById("reports-print");
        await html2pdf().from(el).set({ margin: 6, filename: "relatorio-de-atendimentos.pdf", image: { type: "jpeg", quality: 0.95 }, html2canvas: { scale: 1.5 }, jsPDF: { unit: "mm", format: "a4", orientation: "landscape" } }).save();
      } else {
        const all = await fetchAll(applied);
        let transcripts = null;
        if (transcript) {
          transcripts = await Promise.all(all.map((t) => api.get(`/messages/${t.id}`, { params: { pageNumber: 1 } }).then((r) => ({ ticket: t, messages: r.data.messages || [] })).catch(() => ({ ticket: t, messages: [] }))));
        }
        exportXlsx({ tickets: all, columns, dateFrom: applied.dateFrom, dateTo: applied.dateTo, transcripts });
      }
      setExportOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  const handleSaveFilter = async ({ name, emailWeekly }) => {
    setSaving(true);
    try {
      const { data } = await api.post("/report-filters", { name, filters, emailWeekly });
      setSaved((prev) => [data, ...prev.filter((s) => s.id !== data.id)]);
      setSaveOpen(false);
      toast.success(t("filterSaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const deleteSaved = async (id) => {
    try {
      await api.delete(`/report-filters/${id}`);
      setSaved((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      toastError(err);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageItems = useMemo(() => {
    const items = new Set([1, pageCount, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pageCount));
    const arr = [...items].sort((a, b) => a - b);
    return arr.flatMap((n, i) => (i > 0 && n - arr[i - 1] > 1 ? ["…", n] : [n]));
  }, [page, pageCount]);

  const NpsIcon = (r) => (r >= 4 ? SentimentSatisfiedAltIcon : r >= 3 ? SentimentSatisfiedIcon : SentimentDissatisfiedIcon);
  void tick;

  return (
    <MainContainer>
      <div className={classes.pageHeader}>
        <div>
          <div className={classes.pageTitle}>{t("title")} <span>· {total} {t("tickets")}</span></div>
          <div className={classes.pageSubtitle}>{periodLabel(applied.dateFrom, applied.dateTo)}{updatedAt ? ` · ${updatedAgo(updatedAt)}` : ""}</div>
        </div>
        <div className={classes.actions}>
          <Button className={aeros.ghostButton} startIcon={<BookmarkBorderIcon />} onClick={(e) => (saved.length ? setSavedAnchor(e.currentTarget) : setSaveOpen(true))}>{t("saveFilter")}</Button>
          <Menu open={Boolean(savedAnchor)} anchorEl={savedAnchor} onClose={() => setSavedAnchor(null)} getContentAnchorEl={null} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
            <MenuItem onClick={() => { setSavedAnchor(null); setSaveOpen(true); }}><span className={classes.savedItem}><BookmarkBorderIcon style={{ marginLeft: 0 }} /> {t("saveCurrent")}</span></MenuItem>
            {saved.map((s) => (
              <MenuItem key={s.id} onClick={() => { setSavedAnchor(null); apply({ ...DEFAULT_FILTERS(), ...s.filters, ...(detectPreset(s.filters?.dateFrom, s.filters?.dateTo) ? {} : presetRange("month")) }); }}>
                <span className={classes.savedItem}>{s.name}<DeleteOutlineIcon onClick={(e) => { e.stopPropagation(); deleteSaved(s.id); }} /></span>
              </MenuItem>
            ))}
          </Menu>
          <Button className={aeros.ghostButton} startIcon={<PictureAsPdfOutlinedIcon />} onClick={() => handleExport({ format: "pdf" })} disabled={exporting}>{t("pdf")}</Button>
          <Button className={aeros.gradientButton} startIcon={<GetAppIcon />} onClick={() => setExportOpen(true)}>{t("exportXlsx")}</Button>
        </div>
      </div>

      <div className={classes.pageBody} id="reports-print">
        <ReportsNav />

        <div className={classes.card} data-html2canvas-ignore>
          <div className={classes.row}>
            <div className={classes.segmented}>
              {PRESETS.map((p) => (
                <button key={p.key} type="button" className={`${classes.seg} ${preset === p.key ? classes.segOn : ""}`} onClick={() => apply({ ...filters, ...presetRange(p.key) })}>{p.label}</button>
              ))}
            </div>
            <div className={classes.pill}>
              <EventIcon className={classes.pillIcon} />
              <input type="date" className={classes.dateField} value={filters.dateFrom} onChange={(e) => set({ dateFrom: e.target.value })} onBlur={() => apply()} aria-label={t("dateFrom")} />
              <span>→</span>
              <input type="date" className={classes.dateField} value={filters.dateTo} onChange={(e) => set({ dateTo: e.target.value })} onBlur={() => apply()} aria-label={t("dateTo")} />
            </div>
            <div className={classes.divider} />
            {[
              [t("filters.queue"), "queueIds", lists.queues],
              [t("filters.user"), "userIds", lists.users],
              [t("filters.connection"), "whatsappIds", lists.whatsapps],
            ].map(([label, key, list]) => (
              <div key={key} className={`${classes.pill} ${filters[key].length ? classes.pillOn : ""}`}>
                {label}
                <Select native disableUnderline value={filters[key][0] || ""} onChange={(e) => { const f = { ...filters, [key]: e.target.value ? [Number(e.target.value)] : [] }; apply(f); }} className={classes.pillSelect} inputProps={{ "aria-label": label }}>
                  <option value="">{t("all")}</option>
                  {list.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </Select>
              </div>
            ))}
            <div className={`${classes.pill} ${filters.status.length ? classes.pillOn : ""}`}>
              {t("filters.status")}
              <Select native disableUnderline value={filters.status[0] || ""} onChange={(e) => apply({ ...filters, status: e.target.value ? [e.target.value] : [] })} className={classes.pillSelect} inputProps={{ "aria-label": t("filters.status") }}>
                <option value="">{t("all")}</option>
                {STATUS_PILLS.filter((p) => p.key).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </Select>
            </div>
            <div className={`${classes.pill} ${filters.walletId ? classes.pillOn : ""}`}>
              {t("filters.wallet")}
              <Select native disableUnderline value={filters.walletId} onChange={(e) => apply({ ...filters, walletId: e.target.value })} className={classes.pillSelect} inputProps={{ "aria-label": t("filters.wallet") }}>
                <option value="">{t("allF")}</option>
                {lists.users.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </Select>
            </div>
            <label className={classes.toggleRow}>
              <Switch className={aeros.toggleSwitchSmall} checked={filters.onlyRated} onChange={(e) => apply({ ...filters, onlyRated: e.target.checked })} inputProps={{ "aria-label": t("filters.onlyRated") }} />
              {t("filters.onlyRated")}
            </label>
            {hasActiveFilters(filters) && <button type="button" className={classes.clear} onClick={() => apply({ ...DEFAULT_FILTERS(), dateFrom: filters.dateFrom, dateTo: filters.dateTo })}>{t("clearFilters")}</button>}
          </div>
          <div className={classes.row}>
            <form className={classes.search} onSubmit={(e) => { e.preventDefault(); apply(); }}>
              <SearchIcon />
              <input value={filters.searchParam} onChange={(e) => set({ searchParam: e.target.value })} placeholder={t("searchPlaceholder")} aria-label={t("searchAria")} />
            </form>
            <div className={classes.statusPills}>
              {STATUS_PILLS.map((p) => {
                const on = p.key ? filters.status.length === 1 && filters.status[0] === p.key : !filters.status.length;
                const n = counts[p.field];
                return (
                  <button key={p.key || "all"} type="button" className={`${classes.sPill} ${on ? classes.sPillOn : ""}`} onClick={() => apply({ ...filters, status: p.key ? [p.key] : [] })}>
                    {p.label} {n != null && <span style={{ opacity: on ? 0.7 : 1, color: !on && p.tone ? stateTextColor(p.tone) : undefined }}>{n}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className={classes.kpis}>
          {kpis.map((k) => { const Icon = KPI_ICONS[k.key]; return (
            <div key={k.key} className={classes.kpi}>
              <div className={classes.kpiHead}><Icon style={{ color: k.accent }} />{k.label}</div>
              <div className={classes.kpiValue}>{k.value ?? "—"}</div>
              <div className={`${classes.kpiDelta} ${k.d?.tone === "success" ? aeros.textSuccess : k.d?.tone === "error" ? aeros.textError : ""}`}>{k.d?.text || (summary ? "" : t("summaryUnavailable"))}</div>
            </div>
          ); })}
        </div>

        <div className={aeros.tableCard}>
          <div className={classes.tableWrap}>
            <div>
              <div className={classes.tHead}>
                {["id", "contact", "queue", "user", "wallet", "status", "openClose", "duration", "nps", "sale", "actions"].map((k) => <div key={k}>{t(`columns.${k}`)}</div>)}
              </div>
              {loading && !tickets.length ? <div className={classes.empty}><CircularProgress size={22} /></div>
              : !tickets.length ? <div className={classes.empty}>{t("empty")}</div>
              : tickets.map((row) => {
                const st = statusMeta(row.status);
                const nps = npsMeta(row.NPS);
                const sale = saleCell(row);
                const Icon = nps ? NpsIcon(nps.rate) : null;
                return (
                  <div key={row.id} className={classes.tRow}>
                    <div className={classes.id}>#{row.id}</div>
                    <div className={classes.contact}>
                      <div className={classes.av} style={{ backgroundColor: avatarColorFor(row.contactName || "") }}>{initialsOf(row.contactName || "?")}</div>
                      <div style={{ minWidth: 0 }}>
                        <div className={`${classes.name} ${classes.ell}`}>{row.contactName || t("ticket.noContact")}</div>
                        <div className={`${classes.sub} ${classes.ell}`}>{[row.whatsappName, row.lastMessage].filter(Boolean).join(" · ")}</div>
                      </div>
                    </div>
                    <div>{row.queueName ? <span className={`${classes.chip} ${classes.info}`}><span className={classes.ell}>{row.queueName}</span></span> : <span className={`${classes.chip} ${classes.muted}`}>{t("noQueue")}</span>}</div>
                    <div className={classes.secondary}>{row.userName ? <><div className={classes.avSm} style={{ backgroundColor: avatarColorFor(row.userName) }}>{initialsOf(row.userName)}</div><span className={classes.ell}>{row.userName}</span></> : <span className={classes.dash}>—</span>}</div>
                    <div className={`${classes.secondary} ${classes.ell}`}>{row.walletName || <span className={classes.dash}>—</span>}</div>
                    <div><span className={`${classes.status} ${classes[st.tone]}`}><i />{st.label}</span></div>
                    <div className={classes.dates}>{shortDate(row.createdAt)}<br />{row.closedAt ? shortDate(row.closedAt) : <span>{t("stillOpen")}</span>}</div>
                    <div className={classes.secondary}>{formatDuration(row.supportTime)}</div>
                    <div>{nps ? <div className={`${classes.nps} ${aeros[`text${nps.tone[0].toUpperCase()}${nps.tone.slice(1)}`]}`}><Icon />{nps.rate}</div> : <span className={classes.dash}>—</span>}</div>
                    <div>{sale.kind === "sold" ? <div className={classes.sold}>{sale.text}</div> : sale.kind === "notSold" ? <div className={classes.notSold} title={row.motivoNaoVenda}>{sale.text}</div> : <span className={classes.dash}>—</span>}</div>
                    <div className={classes.rowActions} data-html2canvas-ignore>
                      <Tooltip title={t("logs")}><IconButton className={classes.actBtn} onClick={() => setTicketOpen(row)}><HistoryIcon /></IconButton></Tooltip>
                      <Tooltip title={t("access")}><IconButton className={classes.actBtn} onClick={() => history.push(`/tickets/${row.uuid}`)}><OpenInNewIcon /></IconButton></Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={classes.foot} data-html2canvas-ignore>
            <div>{pageRange(page, pageSize, total)}</div>
            <div className={classes.pageSize}>
              <Select native disableUnderline value={pageSize} onChange={(e) => { const s = Number(e.target.value); setPageSize(s); load(applied, 1, s); }} className={classes.pillSelect} inputProps={{ "aria-label": t("perPageAria") }}>
                {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{t("perPage", { n })}</option>)}
              </Select>
            </div>
            <div className={classes.pages}>
              <button type="button" className={classes.pg} disabled={page <= 1} onClick={() => load(applied, page - 1, pageSize)}>‹</button>
              {pageItems.map((n, i) => (n === "…" ? <span key={`d${i}`} className={classes.dots}>…</span> : <button key={n} type="button" className={`${classes.pg} ${n === page ? classes.pgOn : ""}`} onClick={() => load(applied, n, pageSize)}>{n}</button>))}
              <button type="button" className={classes.pg} disabled={page >= pageCount} onClick={() => load(applied, page + 1, pageSize)}>›</button>
            </div>
          </div>
        </div>
      </div>

      <TicketReportModal open={Boolean(ticketOpen)} ticket={ticketOpen} onClose={() => setTicketOpen(null)} />
      <ExportReportModal open={exportOpen} onClose={() => setExportOpen(false)} onExport={handleExport} total={total} dateFrom={applied.dateFrom} dateTo={applied.dateTo} loading={exporting} />
      {saveOpen && <SaveFilterModal open onClose={() => setSaveOpen(false)} onSave={handleSaveFilter} chips={filterChips()} email={user?.email} loading={saving} />}
    </MainContainer>
  );
};

const stateTextColor = (tone) => ({ info: AEROS.cyan, warning: AEROS.warning, success: AEROS.success }[tone]);

export default Reports;
